import { useEffect, useMemo, useState } from 'react';
import { X, FileCode2, Copy, Download, AlertTriangle, CheckCircle2, ShieldCheck } from 'lucide-react';
import { cn } from '@/lib/cn';
import { prepareNEMSISExport } from '@/lib/nemsis-service';
import { RuleTag } from '@/components/RuleTag';

/**
 * NemsisPreviewModal — renders the generated NEMSIS v3.5.1 XML for
 * this ePCR so the crew (or QA) can inspect the submission payload
 * BEFORE it hits RIEMSIS.
 *
 * Foundation v1.0 §6.3 ("single input → many outputs") + §9.2
 * (Post-time compliance summary) — this is where the single-input
 * principle becomes visible. The rule-tag panel shows which sections
 * of the XML are populated and flags gaps.
 */

interface Props {
  epcrId: string;
  onClose: () => void;
}

export function NemsisPreviewModal({ epcrId, onClose }: Props) {
  const [xml, setXml] = useState<string | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      const result = await prepareNEMSISExport(epcrId);
      if (!alive) return;
      setXml(result?.xml ?? null);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const errs = (result as any)?.errors;
      setErrors(Array.isArray(errs) ? errs.map(String) : []);
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [epcrId]);

  const sections = useMemo(() => inspectSections(xml ?? ''), [xml]);

  async function copy() {
    if (!xml) return;
    try { await navigator.clipboard?.writeText(xml); } catch { /* ignore */ }
  }

  function download() {
    if (!xml) return;
    const blob = new Blob([xml], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `nemsis-${epcrId.slice(0, 8)}.xml`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
      <div className="w-full max-w-5xl max-h-[95vh] flex flex-col border border-slate-700 bg-slate-950 shadow-2xl">
        <header className="flex items-center justify-between border-b border-slate-700 bg-slate-900 px-4 py-2.5">
          <div className="flex items-center gap-2">
            <FileCode2 size={16} className="text-blue-400" />
            <span className="text-sm font-bold uppercase tracking-widest text-slate-100">
              NEMSIS v3.5.1 Export Preview
            </span>
            <RuleTag
              sources={[
                { standard: 'NEMSIS', element: 'v3.5.1 XSD', rationale: 'Federal EMS data standard — 142 ePCR elements.' },
                { standard: 'RIDOH', element: '216-RICR-20-10-2', rationale: 'RIEMSIS electronic submission.' },
              ]}
              compact
            />
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-500 hover:text-slate-200">
            <X size={14} />
          </button>
        </header>

        <div className="flex-1 overflow-hidden grid md:grid-cols-[260px_1fr]">
          {/* Section panel */}
          <aside className="border-r border-slate-800 overflow-y-auto p-2 space-y-2">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 px-1">
              Sections emitted
            </div>
            <ul className="space-y-1">
              {sections.map((s) => (
                <li
                  key={s.tag}
                  className="flex items-center gap-2 border border-slate-800 bg-slate-900/60 px-2 py-1 text-xs"
                >
                  {s.present ? (
                    <CheckCircle2 size={11} className="text-emerald-400 shrink-0" />
                  ) : (
                    <AlertTriangle size={11} className="text-slate-600 shrink-0" />
                  )}
                  <span className="flex-1 font-mono text-[11px]">
                    {s.tag}
                  </span>
                  <span className={cn(
                    'text-[10px] tabular-nums',
                    s.present ? 'text-emerald-300' : 'text-slate-600',
                  )}>
                    {s.count}
                  </span>
                </li>
              ))}
            </ul>

            {errors.length > 0 && (
              <div className="border border-red-800 bg-red-950/40 p-2">
                <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-red-300">
                  <AlertTriangle size={11} /> Validator
                </div>
                <ul className="mt-1 space-y-0.5 text-[11px] text-red-200">
                  {errors.slice(0, 6).map((e, i) => <li key={i}>· {e}</li>)}
                </ul>
              </div>
            )}

            <div className="border border-slate-800 bg-slate-900/60 p-2 text-[11px] leading-snug text-slate-400">
              <div className="flex items-center gap-1 font-bold uppercase tracking-widest text-[10px] text-slate-300">
                <ShieldCheck size={11} className="text-emerald-400" /> Send path
              </div>
              The XML emitted here is what RIEMSIS receives. Tap
              <strong> Download </strong> to inspect offline or
              <strong> Copy </strong> to paste into an external
              validator.
            </div>
          </aside>

          {/* XML content */}
          <div className="flex flex-col overflow-hidden">
            <pre className="flex-1 overflow-auto bg-slate-950 p-3 font-mono text-[11px] leading-snug text-slate-200 whitespace-pre">
              {loading ? 'Generating…' : xml ?? '— no XML produced —'}
            </pre>
          </div>
        </div>

        <footer className="flex items-center justify-between border-t border-slate-700 bg-slate-900 px-4 py-2">
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
            {xml ? `${xml.length.toLocaleString()} chars · ${sections.filter((s) => s.present).length}/${sections.length} sections populated` : 'Preparing…'}
          </div>
          <div className="flex gap-2">
            <button onClick={copy} disabled={!xml} className="btn-secondary text-xs gap-1">
              <Copy size={12} /> Copy
            </button>
            <button onClick={download} disabled={!xml} className="btn-secondary text-xs gap-1">
              <Download size={12} /> Download
            </button>
            <button onClick={onClose} className="btn-primary text-xs">Close</button>
          </div>
        </footer>
      </div>
    </div>
  );
}

interface SectionInspect {
  tag: string;
  present: boolean;
  count: number;
}

const TRACKED_SECTIONS = [
  'eRecord', 'eResponse', 'eDispatch', 'eTimes', 'eScene',
  'ePatient', 'eSituation', 'eHistory', 'eInjury', 'eArrest',
  'eVitals', 'eExam', 'eProtocols', 'eMedications', 'eProcedures',
  'eDisposition', 'eOutcome', 'eNarrative',
];

function inspectSections(xml: string): SectionInspect[] {
  return TRACKED_SECTIONS.map((tag) => {
    const re = new RegExp(`<${tag}(?:\\s|>)`, 'g');
    const matches = xml.match(re);
    return {
      tag,
      present: !!matches,
      count: matches?.length ?? 0,
    };
  });
}
